import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, NavLink, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import posts from './posts.json';

const API_BASE = '/api';
const POSTS_PER_PAGE = 10;

function Nav({ showAll = true }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sitesOpen, setSitesOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [personalOpen, setPersonalOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);

  useEffect(() => {
    fetch(API_BASE + '/check-ip')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.allowed) {
          setIsAllowed(true);
        }
      })
      .catch(() => {});
  }, []);

  if (!showAll || !isAllowed) return null;

  const toggleSubmenu = (e, setter, current) => {
    e.preventDefault();
    e.stopPropagation();
    setter(!current);
  };

  return (
    <nav className="hamburger-nav">
      <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
        <span className="hamburger-icon">☰</span>
      </button>
      <Link to="/" className="nav-brand">ASHER SWING</Link>
      {menuOpen && (
        <div className="hamburger-menu">
          <NavLink to="/manager" onClick={() => setMenuOpen(false)}>Manager</NavLink>
          <div className="menu-group">
            <button className="menu-group-title" onClick={(e) => toggleSubmenu(e, setSitesOpen, sitesOpen)}>Sites {sitesOpen ? '▼' : '▶'}</button>
            {sitesOpen && (
              <div className="menu-submenu">
                <NavLink to="/site" onClick={() => setMenuOpen(false)}>Site</NavLink>
                <NavLink to="/pages" onClick={() => setMenuOpen(false)}>Page</NavLink>
                <NavLink to="/sources" onClick={() => setMenuOpen(false)}>Source</NavLink>
                <NavLink to="/thomas" onClick={() => setMenuOpen(false)}>Thomas</NavLink>
                <NavLink to="/maps" onClick={() => setMenuOpen(false)}>Maps</NavLink>
                <NavLink to="/shopping" onClick={() => setMenuOpen(false)}>Shopping</NavLink>
              </div>
            )}
          </div>
          <div className="menu-group">
            <button className="menu-group-title" onClick={(e) => toggleSubmenu(e, setMailOpen, mailOpen)}>Mail {mailOpen ? '▼' : '▶'}</button>
            {mailOpen && (
              <div className="menu-submenu">
                <NavLink to="/email" onClick={() => setMenuOpen(false)}>Email</NavLink>
                <NavLink to="/gmail" onClick={() => setMenuOpen(false)}>Gmail</NavLink>
                <NavLink to="/calendar" onClick={() => setMenuOpen(false)}>Calendar</NavLink>
                <NavLink to="/2do" onClick={() => setMenuOpen(false)}>2Do</NavLink>
              </div>
            )}
          </div>
          <div className="menu-group">
            <button className="menu-group-title" onClick={(e) => toggleSubmenu(e, setMediaOpen, mediaOpen)}>Media {mediaOpen ? '▼' : '▶'}</button>
            {mediaOpen && (
              <div className="menu-submenu">
                <NavLink to="/youtube" onClick={() => setMenuOpen(false)}>YouTube</NavLink>
                <NavLink to="/music" onClick={() => setMenuOpen(false)}>Music</NavLink>
                <NavLink to="/movies" onClick={() => setMenuOpen(false)}>Movies</NavLink>
              </div>
            )}
          </div>
          <div className="menu-group">
            <button className="menu-group-title" onClick={(e) => toggleSubmenu(e, setWorkOpen, workOpen)}>Work {workOpen ? '▼' : '▶'}</button>
            {workOpen && (
              <div className="menu-submenu">
                <NavLink to="/trade" onClick={() => setMenuOpen(false)}>Trade</NavLink>
                <NavLink to="/onewerx" onClick={() => setMenuOpen(false)}>OneWerx</NavLink>
                <NavLink to="/ai" onClick={() => setMenuOpen(false)}>AI</NavLink>
                <NavLink to="/re" onClick={() => setMenuOpen(false)}>RE</NavLink>
                <a href="https://export.asheram.com" target="_blank" rel="noopener noreferrer">Export</a>
              </div>
            )}
          </div>
          <div className="menu-group">
            <button className="menu-group-title" onClick={(e) => toggleSubmenu(e, setPersonalOpen, personalOpen)}>Personal {personalOpen ? '▼' : '▶'}</button>
            {personalOpen && (
              <div className="menu-submenu">
                <NavLink to="/personal" onClick={() => setMenuOpen(false)}>Sites</NavLink>
                <NavLink to="/blog" onClick={() => setMenuOpen(false)}>Blog</NavLink>
                <NavLink to="/dj" onClick={() => setMenuOpen(false)}>DJ</NavLink>
                <a href="https://myfitnesspal.com" target="_blank" rel="noopener noreferrer">Health</a>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

function Header() {
  return (
    <header className="app-header">
      <Nav />
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <p>Asher Swing - asheram.com</p>
    </footer>
  );
}

// Manager Page - Project Management Dashboard
function Manager() {
  const [domains, setDomains] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  // Project definitions with their domains
  const projects = [
    { name: 'Thomas', path: '/thomas', domains: ['3fuel.com', 'food33.com', 'kmkle.com', 'metal33.com', 'oil33.com', 'qqarbon.com', 'rb0t.com', 'solrze.com', 'syrvyr.com'], desc: 'Manage React e-commerce sites' },
    { name: 'OneWerx', path: '/onewerx', domains: ['onewerx.com', 'pmley.com'], desc: 'Manage digital agency sites' },
    { name: 'AI', path: '/ai', domains: ['pllur.com', 'rprtz.com', 'bundalini.com', 'gr11d.com', 'elyvt.com', 'lyfter.com'], desc: 'Manage AI project sites' },
    { name: 'RE', path: '/re', domains: ['xp0e.com', 'worke.ca', 'equityfair.com', 'hhomez.com'], desc: 'Manage real estate sites' },
    { name: 'Site', path: '/site', domains: ['drnzz.com', 'evcle.com', 'kosmae.com', '4emd.com', '47ly.com', 'mmineral.com', 'llaundromat.com', 'trlze.com', 'fbryk.com', 'fish999.com', 'fnzly.com', 'kidzley.com', 'luxrry.com', 'rrenew.com', 'snkre.com', 'sprtze.com'], desc: 'Manage template sites' },
    { name: 'Personal', path: '/personal', domains: ['asheram.com', 'ynglee.com', 'resultt.com', 'bllnc.com', 'blissark.com', 'ivoze.com', 'rrupa.com'], desc: 'Manage personal & client sites' },
    { name: 'ToDo', path: '/domains', domains: [], desc: 'Manage remaining domains' }
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [domainsRes, analyticsRes] = await Promise.all([
        fetch(API_BASE + '/domains'),
        fetch(API_BASE + '/analytics/summary')
      ]);

      const domainsData = await domainsRes.json();
      const analyticsData = await analyticsRes.json();

      if (domainsData.success) setDomains(domainsData.domains);
      if (analyticsData.success) setAnalytics(analyticsData.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const getAnalyticsForDomain = (domain) => {
    return analytics[domain] || analytics['www.' + domain] || { sessions: 0, engagedSessions: 0, users: 0, pageviews: 0, realSessions: 0 };
  };

  // Calculate stats for each project
  const projectStats = projects.map(project => {
    const projectDomains = project.domains.length > 0 ? project.domains : [];
    const stats = projectDomains.reduce((acc, domain) => {
      const domainStats = getAnalyticsForDomain(domain);
      return {
        realSessions: acc.realSessions + domainStats.realSessions,
        pageviews: acc.pageviews + domainStats.pageviews
      };
    }, { realSessions: 0, pageviews: 0 });
    return { ...project, stats, domainCount: projectDomains.length };
  });

  // Calculate totals
  const totalStats = domains.reduce((acc, d) => {
    const stats = getAnalyticsForDomain(d.domain_name);
    return {
      realSessions: acc.realSessions + stats.realSessions,
      pageviews: acc.pageviews + stats.pageviews
    };
  }, { realSessions: 0, pageviews: 0 });

  return (
    <>
      <Helmet>
        <title>Manager - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Project Manager</h2>
        <p className="subtitle">Central hub for managing domain projects</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="stats-summary">
              <div className="stat-box">
                <span className="stat-value">{domains.length}</span>
                <span className="stat-label">Total Domains</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.realSessions.toLocaleString()}</span>
                <span className="stat-label">Real Sessions</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.pageviews.toLocaleString()}</span>
                <span className="stat-label">Pageviews</span>
              </div>
            </div>

            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Description</th>
                    <th>Domains</th>
                    <th>Real Sessions</th>
                    <th>Pageviews</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {projectStats.map(project => (
                    <tr key={project.name}>
                      <td><strong>{project.name}</strong></td>
                      <td className="description">{project.desc}</td>
                      <td>{project.domainCount || '-'}</td>
                      <td>{project.stats.realSessions.toLocaleString()}</td>
                      <td>{project.stats.pageviews.toLocaleString()}</td>
                      <td><Link to={project.path} className="action-link">Manage</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={{ marginTop: '32px', marginBottom: '16px' }}>Analytics & Tools</h3>
            <div className="home-cards" style={{ marginTop: '0' }}>
              <Link to="/pages" className="home-card">
                <h3>Pages</h3>
                <p>Monitor top performing pages</p>
              </Link>
              <Link to="/sources" className="home-card">
                <h3>Sources</h3>
                <p>Analyze traffic sources</p>
              </Link>
              <Link to="/trade" className="home-card">
                <h3>Trade</h3>
                <p>Manage data sources</p>
              </Link>
              <Link to="/empty-categories" className="home-card">
                <h3>Empty Categories</h3>
                <p>1,942 categories without products</p>
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Home Page
function Home() {
  return (
    <>
      <Helmet>
        <title>Asher Swing</title>
        <meta name="description" content="Asher Swing - Developer & Domain Portfolio" />
      </Helmet>
      <div className="home-section">
        <div className="home-cards">
          <Link to="/domains" className="home-card">
            <h2>Domains</h2>
            <p>Monitor domain portfolio with GA4 analytics</p>
          </Link>
          <Link to="/pages" className="home-card">
            <h2>Pages</h2>
            <p>Most engaged pages across all domains</p>
          </Link>
          <Link to="/resume" className="home-card">
            <h2>Resume</h2>
            <p>Technical Marketing Manager</p>
          </Link>
          <Link to="/blog" className="home-card">
            <h2>Blog</h2>
            <p>Spirituality, meditation, yoga</p>
          </Link>
        </div>
      </div>
    </>
  );
}

// CMO Homepage
function CMOHomepage() {
  return (
    <div className="cmo-homepage">
      <header className="cmo-masthead">
        <div className="cmo-container">
          <div className="masthead-subheading">Strategic Marketing Leadership</div>
          <h1 className="masthead-heading">Fractional CMO</h1>
          <p className="masthead-desc">Executive-level marketing expertise without the full-time commitment</p>
          <a href="#services" className="cmo-btn">Learn More</a>
        </div>
      </header>

      <section className="cmo-section" id="services">
        <div className="cmo-container">
          <h2 className="section-heading">Services</h2>
          <p className="section-subheading">Strategic marketing leadership tailored to your business needs</p>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">🎯</div>
              <h4>Marketing Strategy</h4>
              <p>Develop comprehensive go-to-market strategies that align with your business objectives and drive measurable growth.</p>
            </div>
            <div className="service-card">
              <div className="service-icon">👥</div>
              <h4>Team Leadership</h4>
              <p>Build, mentor, and lead high-performing marketing teams. Establish processes and frameworks for sustainable success.</p>
            </div>
            <div className="service-card">
              <div className="service-icon">📈</div>
              <h4>Growth Acceleration</h4>
              <p>Implement data-driven marketing programs that generate qualified leads, increase conversions, and maximize ROI.</p>
            </div>
            <div className="service-card">
              <div className="service-icon">💡</div>
              <h4>Brand Development</h4>
              <p>Craft compelling brand narratives and positioning that differentiate you in the market and resonate with your audience.</p>
            </div>
            <div className="service-card">
              <div className="service-icon">⚙️</div>
              <h4>Marketing Operations</h4>
              <p>Optimize your marketing technology stack, automate workflows, and establish metrics that matter.</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🤝</div>
              <h4>Investor Relations</h4>
              <p>Support fundraising efforts with compelling pitch materials, market analysis, and growth narratives.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cmo-section cmo-section-alt" id="about">
        <div className="cmo-container">
          <h2 className="section-heading">About</h2>
          <p className="section-subheading">Experienced marketing executive</p>
          <div className="about-content">
            <p>With over 15 years of experience driving growth for startups and enterprises alike, I bring a unique blend of strategic vision and hands-on execution to every engagement.</p>
            <p>My background spans B2B SaaS, eCommerce, fintech, and media—giving me the versatility to adapt quickly to your industry's unique challenges and opportunities.</p>
            <ul className="about-highlights">
              <li>Former marketing leader at Google, Verizon, and multiple high-growth startups</li>
              <li>Track record of 10x pipeline growth and 300%+ ROI improvements</li>
              <li>Deep expertise in digital marketing, demand generation, and brand building</li>
              <li>Certified Scrum Master with technical background in computer science</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="cmo-section" id="contact">
        <div className="cmo-container">
          <h2 className="section-heading">Let's Talk</h2>
          <p className="section-subheading">Ready to accelerate your growth?</p>
          <div className="contact-info">
            <p>Email: <a href="mailto:asherswing@gmail.com">asherswing@gmail.com</a></p>
            <p>Phone: (212) 729-1154</p>
            <p>Location: Jersey City, NJ</p>
          </div>
        </div>
      </section>
    </div>
  );
}

// Resume Page - Fractional CMO Landing Page (Caffeine Marketing Style)
function Resume() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Asher Swing - Fractional CMO",
    "description": "Fractional CMO for B2B companies. Strategy + execution.",
    "url": "https://asheram.com/",
    "telephone": "(212) 729-1154",
    "email": "asherswing@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Jersey City",
      "addressRegion": "NJ",
      "addressCountry": "US"
    },
    "founder": {
      "@type": "Person",
      "name": "Asher Swing",
      "jobTitle": "Fractional CMO",
      "alumniOf": "University of Maryland"
    },
    "areaServed": "United States",
    "serviceType": ["Fractional CMO", "B2B Marketing", "Marketing Strategy", "Demand Generation"]
  };

  return (
    <>
      <Helmet>
        <title>Fractional CMO | Asher Swing</title>
        <meta name="description" content="Fractional CMO for B2B companies. Strategy + execution. 20+ years experience at Google, Verizon, Bank of America." />
        <meta property="og:title" content="Fractional CMO | Asher Swing" />
        <meta property="og:description" content="B2B growth engine. Executive-level marketing leadership without the full-time cost." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://asheram.com/" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="cmo-hero">
        <div className="cmo-hero-content">
          <h1 className="cmo-hero-title">Fractional CMO</h1>
          <p className="cmo-hero-subtitle">
            B2B growth engine. Strategy + execution. Scale with confidence.
          </p>
          <a href="mailto:asherswing@gmail.com?subject=Strategy Call Request" className="cmo-btn-primary">Book a Strategy Call</a>
        </div>
      </section>

      {/* Value Props */}
      <section className="cmo-value-props">
        <div className="value-prop">
          <div className="value-icon">✓</div>
          <h3>Proven Success</h3>
          <p>20+ years driving growth at Google, Verizon, and Bank of America</p>
        </div>
        <div className="value-prop">
          <div className="value-icon">⚡</div>
          <h3>Strategy + Execution</h3>
          <p>Not just plans—I roll up my sleeves and get it done</p>
        </div>
        <div className="value-prop">
          <div className="value-icon">🎯</div>
          <h3>B2B Expert</h3>
          <p>Deep expertise in SaaS, eCommerce, fintech, and enterprise</p>
        </div>
      </section>

      {/* Credentials */}
      <section className="cmo-credentials">
        <div className="credential-logos">
          <span className="credential-item">Google</span>
          <span className="credential-item">Verizon</span>
          <span className="credential-item">Bank of America</span>
          <span className="credential-item">Pepsi</span>
          <span className="credential-item">Ford</span>
          <span className="credential-item">MTV</span>
        </div>
      </section>

      {/* Process Section */}
      <section className="cmo-process">
        <h2 className="section-title">How It Works</h2>
        <p className="section-subtitle">A proven process to accelerate your growth</p>
        <div className="process-steps">
          <div className="process-step">
            <div className="step-number">1</div>
            <h3>Discovery</h3>
            <p>We meet to assess your current marketing, goals, and challenges</p>
          </div>
          <div className="process-step">
            <div className="step-number">2</div>
            <h3>Strategy</h3>
            <p>I build a customized growth plan tailored to your business</p>
          </div>
          <div className="process-step">
            <div className="step-number">3</div>
            <h3>Execution</h3>
            <p>We implement lead generation and revenue-driving campaigns</p>
          </div>
          <div className="process-step">
            <div className="step-number">4</div>
            <h3>Scale</h3>
            <p>Unlock predictable, sustainable growth for your business</p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="cmo-services">
        <h2 className="section-title">What You Get</h2>
        <p className="section-subtitle">Full-service marketing leadership at a fraction of the cost</p>
        <div className="services-grid">
          <div className="service-box">
            <h4>Marketing Strategy</h4>
            <p>Comprehensive go-to-market strategies aligned with your business objectives</p>
          </div>
          <div className="service-box">
            <h4>Team Leadership</h4>
            <p>Build, mentor, and lead high-performing marketing teams</p>
          </div>
          <div className="service-box">
            <h4>Demand Generation</h4>
            <p>Data-driven programs that generate qualified leads and maximize ROI</p>
          </div>
          <div className="service-box">
            <h4>Brand Development</h4>
            <p>Compelling narratives and positioning that differentiate you in the market</p>
          </div>
          <div className="service-box">
            <h4>Marketing Operations</h4>
            <p>Optimize your tech stack, automate workflows, establish metrics</p>
          </div>
          <div className="service-box">
            <h4>Analytics & Reporting</h4>
            <p>Real-time dashboards and insights to track performance</p>
          </div>
          <div className="service-box">
            <h4>Content Marketing</h4>
            <p>SEO-optimized content that attracts and converts your ideal customers</p>
          </div>
          <div className="service-box">
            <h4>Paid Media</h4>
            <p>Google Ads, LinkedIn, Facebook campaigns optimized for B2B</p>
          </div>
          <div className="service-box">
            <h4>Email Marketing</h4>
            <p>Nurture sequences and campaigns that drive engagement</p>
          </div>
          <div className="service-box">
            <h4>SEO</h4>
            <p>Organic search optimization to capture high-intent traffic</p>
          </div>
          <div className="service-box">
            <h4>ABM</h4>
            <p>Account-based marketing for enterprise sales cycles</p>
          </div>
          <div className="service-box">
            <h4>Investor Relations</h4>
            <p>Pitch materials, market analysis, and growth narratives for fundraising</p>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="cmo-results">
        <h2 className="section-title">Results That Matter</h2>
        <div className="results-grid">
          <div className="result-item">
            <div className="result-number">$2B+</div>
            <p>E-commerce revenue optimized at Verizon</p>
          </div>
          <div className="result-item">
            <div className="result-number">$100M+</div>
            <p>Client advertising managed at Google</p>
          </div>
          <div className="result-item">
            <div className="result-number">Page 1</div>
            <p>Google ranking achieved for DailyFX</p>
          </div>
          <div className="result-item">
            <div className="result-number">7M</div>
            <p>Users managed at Paltalk</p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="cmo-about" id="about">
        <div className="about-container">
          <h2 className="section-title">About</h2>
          <p className="section-subtitle">20+ years of marketing leadership experience</p>
          <div className="about-content-box">
            <p>I'm a Certified Scrum Master with a Computer Science degree and full lifecycle digital marketing expertise. My background spans B2B SaaS, eCommerce, fintech, and media—giving me the versatility to adapt quickly to your industry's unique challenges.</p>
            <ul className="about-highlights">
              <li><strong>Google:</strong> Global Insight Manager for $100M+ clients like Pepsi and Mars</li>
              <li><strong>Verizon:</strong> Technical Marketing Manager for $2B+ eCommerce</li>
              <li><strong>Bank of America:</strong> Campaign tracking and reporting for digital programs</li>
              <li><strong>Certifications:</strong> Scrum Master (CSM), Computer Science BS</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="cmo-experience" id="experience">
        <h2 className="section-title">Experience</h2>
        <div className="experience-timeline">
          <div className="exp-item">
            <div className="exp-company">OneWerx</div>
            <div className="exp-role">Marketing Manager</div>
            <div className="exp-date">2017 - Present</div>
            <p>Digital agency—eCommerce optimization, CI/CD pipelines, HubSpot, SEO for Fortune 500 clients</p>
          </div>
          <div className="exp-item">
            <div className="exp-company">Google</div>
            <div className="exp-role">Global Insight Manager</div>
            <div className="exp-date">2015 - 2017</div>
            <p>Custom data-driven solutions for $100M+ clients. Built statistical models in R for media optimization</p>
          </div>
          <div className="exp-item">
            <div className="exp-company">Verizon</div>
            <div className="exp-role">Technical Marketing Manager</div>
            <div className="exp-date">2013 - 2015</div>
            <p>Big Data project for $2B+ VerizonWireless.com performance analytics</p>
          </div>
          <div className="exp-item">
            <div className="exp-company">Bank of America</div>
            <div className="exp-role">Technical Project Manager</div>
            <div className="exp-date">2012 - 2013</div>
            <p>Campaign tracking and reporting for BankofAmerica.com digital marketing</p>
          </div>
          <div className="exp-item">
            <div className="exp-company">FXCM</div>
            <div className="exp-role">Marketing Manager</div>
            <div className="exp-date">2007 - 2012</div>
            <p>Led DailyFX.com to become largest lead source for retail Forex broker</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="cmo-faq">
        <h2 className="section-title">FAQ</h2>
        <div className="faq-list">
          <div className="faq-item">
            <h4>What is a Fractional CMO?</h4>
            <p>A Fractional CMO is an experienced marketing executive who works with your company on a part-time or contract basis. You get C-level marketing leadership without the full-time salary and benefits cost.</p>
          </div>
          <div className="faq-item">
            <h4>Who is this for?</h4>
            <p>B2B companies that need strategic marketing leadership but aren't ready for a full-time CMO. Typically companies with $1M-$50M in revenue looking to scale.</p>
          </div>
          <div className="faq-item">
            <h4>How does engagement work?</h4>
            <p>We start with a strategy session to understand your business. Then I typically work 10-20 hours per week on strategy, team leadership, and execution oversight.</p>
          </div>
          <div className="faq-item">
            <h4>What's the investment?</h4>
            <p>Fixed monthly retainer based on scope. Typically 1/3 to 1/4 the cost of a full-time CMO with the same level of expertise.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cmo-cta">
        <h2>Ready to Accelerate Your Growth?</h2>
        <p>Let's talk about how I can help your business scale.</p>
        <div className="cta-details">
          <div className="cta-step">
            <span className="cta-time">1 min</span>
            <span>Quick intro</span>
          </div>
          <div className="cta-step">
            <span className="cta-time">27 min</span>
            <span>Strategy insights</span>
          </div>
          <div className="cta-step">
            <span className="cta-time">2 min</span>
            <span>Next steps</span>
          </div>
        </div>
        <a href="mailto:asherswing@gmail.com?subject=Strategy Call Request" className="cmo-btn-primary cmo-btn-large">Book a Strategy Call</a>
        <div className="contact-info-row">
          <span>asherswing@gmail.com</span>
          <span>•</span>
          <span>(212) 729-1154</span>
          <span>•</span>
          <span>Jersey City, NJ</span>
        </div>
      </section>
    </>
  );
}

// Domain Monitor Page
function DomainMonitor() {
  const [domains, setDomains] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // Thomas domains to exclude (shown in Thomas page)
  const thomasDomains = ['3fuel.com', 'food33.com', 'kmkle.com', 'metal33.com', 'oil33.com', 'qqarbon.com', 'rb0t.com', 'solrze.com', 'syrvyr.com'];
  // OneWerx domain (shown in OneWerx page)
  const onewerxDomains = ['onewerx.com', 'pmley.com'];
  // Personal domains (shown in Personal page)
  const personalDomains = ['asheram.com', 'ynglee.com', 'resultt.com', 'bllnc.com', 'blissark.com', 'ivoze.com', 'rrupa.com'];
  // AI domains (shown in AI page)
  const aiDomains = ['pllur.com', 'rprtz.com', 'bundalini.com', 'gr11d.com', 'elyvt.com', 'lyfter.com'];
  // RE domains (shown in RE page)
  const reDomains = ['xp0e.com', 'worke.ca', 'equityfair.com', 'hhomez.com'];
  // New domains (shown in New page)
  const newDomains = ['drnzz.com', 'evcle.com', 'kosmae.com', '4emd.com', '47ly.com', 'mmineral.com', 'llaundromat.com', 'trlze.com', 'fbryk.com', 'fish999.com', 'fnzly.com', 'kidzley.com', 'luxrry.com', 'rrenew.com', 'snkre.com', 'sprtze.com'];
  // Domains to hide completely
  const hiddenDomains = ['adalty.com', 'cncrge.com'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [domainsRes, analyticsRes] = await Promise.all([
        fetch(API_BASE + '/domains'),
        fetch(API_BASE + '/analytics/summary')
      ]);

      const domainsData = await domainsRes.json();
      const analyticsData = await analyticsRes.json();

      if (domainsData.success) {
        // Exclude Thomas, OneWerx, Personal, AI, RE, New, and hidden domains
        setDomains(domainsData.domains.filter(d =>
          !thomasDomains.includes(d.domain_name) &&
          !onewerxDomains.includes(d.domain_name) &&
          !personalDomains.includes(d.domain_name) &&
          !aiDomains.includes(d.domain_name) &&
          !reDomains.includes(d.domain_name) &&
          !newDomains.includes(d.domain_name) &&
          !hiddenDomains.includes(d.domain_name)
        ));
      }
      if (analyticsData.success) setAnalytics(analyticsData.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const getAnalyticsForDomain = (domain) => {
    // Try with and without www
    return analytics[domain] || analytics['www.' + domain] || { sessions: 0, engagedSessions: 0, users: 0, pageviews: 0, realSessions: 0 };
  };

  const filteredDomains = domains.filter(d => {
    if (filter === 'all') return true;
    if (filter === 'pluto') return d.server_name === 'pluto';
    if (filter === 'venus') return d.server_name === 'venus';
    return true;
  });

  const totalStats = filteredDomains.reduce((acc, d) => {
    const stats = getAnalyticsForDomain(d.domain_name);
    return {
      sessions: acc.sessions + stats.sessions,
      engagedSessions: acc.engagedSessions + stats.engagedSessions,
      users: acc.users + stats.users,
      pageviews: acc.pageviews + stats.pageviews,
      realSessions: acc.realSessions + stats.realSessions
    };
  }, { sessions: 0, engagedSessions: 0, users: 0, pageviews: 0, realSessions: 0 });

  return (
    <>
      <Helmet>
        <title>Domain Monitor - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Domain Monitor</h2>
        <p className="subtitle">Last 7 days analytics from GA4</p>

        <div className="filter-bar">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All ({domains.length})</button>
          <button className={filter === 'pluto' ? 'active' : ''} onClick={() => setFilter('pluto')}>Pluto</button>
          <button className={filter === 'venus' ? 'active' : ''} onClick={() => setFilter('venus')}>Venus</button>
        </div>

        <div className="stats-summary">
          <div className="stat-box">
            <span className="stat-value">{totalStats.sessions.toLocaleString()}</span>
            <span className="stat-label">Sessions</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{totalStats.engagedSessions.toLocaleString()}</span>
            <span className="stat-label">Engaged</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{totalStats.realSessions.toLocaleString()}</span>
            <span className="stat-label">Real</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{totalStats.users.toLocaleString()}</span>
            <span className="stat-label">Users</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{totalStats.pageviews.toLocaleString()}</span>
            <span className="stat-label">Pageviews</span>
          </div>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="domains-table">
            <table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Description</th>
                  <th>Server</th>
                  <th>Sessions</th>
                  <th>Engaged</th>
                  <th>Real</th>
                  <th>Users</th>
                  <th>Pageviews</th>
                </tr>
              </thead>
              <tbody>
                {filteredDomains.map(d => {
                  const stats = getAnalyticsForDomain(d.domain_name);
                  return (
                    <tr key={d.domain_name}>
                      <td>
                        <a href={"https://" + d.domain_name} target="_blank" rel="noopener noreferrer">
                          {d.domain_name}
                        </a>
                      </td>
                      <td className="description">{d.purpose || d.company_name || '-'}</td>
                      <td>{d.server_name || '-'}</td>
                      <td>{stats.sessions.toLocaleString()}</td>
                      <td>{stats.engagedSessions.toLocaleString()}</td>
                      <td>{stats.realSessions.toLocaleString()}</td>
                      <td>{stats.users.toLocaleString()}</td>
                      <td>{stats.pageviews.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// Top Pages
function TopPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(API_BASE + '/analytics/top-pages')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          const sorted = [...data.pages].sort((a, b) => b.sessions - a.sessions);
          setPages(sorted);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Pages - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Pages</h2>
        <p className="subtitle">Most engaged pages across all domains (last 7 days)</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="domains-table">
            <table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Page</th>
                  <th>Sessions</th>
                  <th>Engaged</th>
                  <th>Real</th>
                  <th>Pageviews</th>
                </tr>
              </thead>
              <tbody>
                {pages.map((p, i) => (
                  <tr key={i}>
                    <td>
                      <a href={"https://" + p.hostname + p.path} target="_blank" rel="noopener noreferrer">
                        {p.hostname}
                      </a>
                    </td>
                    <td className="description">{p.path}</td>
                    <td>{p.sessions.toLocaleString()}</td>
                    <td>{p.engagedSessions.toLocaleString()}</td>
                    <td>{p.sessions.toLocaleString()}</td>
                    <td>{p.pageviews.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// Sources Page
function Sources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(API_BASE + '/analytics/sources')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          const sorted = [...data.sources].sort((a, b) => b.sessions - a.sessions);
          setSources(sorted);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Sources - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Sources</h2>
        <p className="subtitle">Traffic sources across all domains (last 7 days)</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="domains-table">
            <table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Source</th>
                  <th>Medium</th>
                  <th>Sessions</th>
                  <th>Engaged</th>
                  <th>Real</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <a href={"https://" + s.hostname} target="_blank" rel="noopener noreferrer">
                        {s.hostname}
                      </a>
                    </td>
                    <td>{s.source}</td>
                    <td>{s.medium}</td>
                    <td>{s.sessions.toLocaleString()}</td>
                    <td>{s.engagedSessions.toLocaleString()}</td>
                    <td>{s.realSessions.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// Trade Page - Trading Bot Data Sources
function Trade() {
  const [sources, setSources] = useState([]);
  const [summary, setSummary] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sourcesRes, summaryRes] = await Promise.all([
        fetch(API_BASE + '/trade/sources'),
        fetch(API_BASE + '/trade/summary')
      ]);

      const sourcesData = await sourcesRes.json();
      const summaryData = await summaryRes.json();

      if (sourcesData.success) setSources(sourcesData.sources);
      if (summaryData.success) {
        setSummary(summaryData.summary);
        setStatusCounts(summaryData.statusCounts || {});
      }
    } catch (err) {
      console.error('Error fetching trade data:', err);
    }
    setLoading(false);
  };

  const filteredSources = sources.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'active') return s.status === 'ACTIVE';
    if (filter === 'free') return s.source_type === 'FREE';
    if (filter === 'paid') return s.source_type === 'PAID';
    return true;
  });

  const totalActive = statusCounts['ACTIVE'] || 0;
  const totalSources = sources.length;
  const totalCoverage = sources.reduce((acc, s) => acc + (s.coverage_symbols || 0), 0);

  const getStatusColor = (status) => {
    switch(status) {
      case 'ACTIVE': return '#22c55e';
      case 'TESTING': return '#f59e0b';
      case 'DEMO': return '#6b7280';
      case 'INACTIVE': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <>
      <Helmet>
        <title>Trade - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Trading Bot</h2>
        <p className="subtitle">Market data sources for trading bot on Venus/Bundalini</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="stats-summary">
              <div className="stat-box">
                <span className="stat-value">{totalSources}</span>
                <span className="stat-label">Data Sources</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalActive}</span>
                <span className="stat-label">Active</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalCoverage.toLocaleString()}</span>
                <span className="stat-label">Symbols</span>
              </div>
            </div>

            <div className="filter-bar">
              <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All ({totalSources})</button>
              <button className={filter === 'active' ? 'active' : ''} onClick={() => setFilter('active')}>Active ({totalActive})</button>
              <button className={filter === 'free' ? 'active' : ''} onClick={() => setFilter('free')}>Free</button>
              <button className={filter === 'paid' ? 'active' : ''} onClick={() => setFilter('paid')}>Paid</button>
            </div>

            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Quality</th>
                    <th>Cost/mo</th>
                    <th>Symbols</th>
                    <th>RT</th>
                    <th>WS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSources.map(s => (
                    <tr key={s.source_name}>
                      <td><strong>{s.source_name}</strong></td>
                      <td>{s.source_type}</td>
                      <td style={{ color: getStatusColor(s.status) }}>{s.status}</td>
                      <td>{s.data_quality_rating}/10</td>
                      <td>{s.monthly_cost > 0 ? `$${s.monthly_cost}` : 'Free'}</td>
                      <td>{s.coverage_symbols?.toLocaleString() || '-'}</td>
                      <td>{s.real_time_available ? '✓' : '-'}</td>
                      <td>{s.websocket_available ? '✓' : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={{ marginTop: '32px', marginBottom: '16px' }}>Summary by Type</h3>
            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Sources</th>
                    <th>Active</th>
                    <th>Real-time</th>
                    <th>WebSocket</th>
                    <th>Avg Quality</th>
                    <th>Monthly Cost</th>
                    <th>Symbol Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map(s => (
                    <tr key={s.source_type}>
                      <td><strong>{s.source_type}</strong></td>
                      <td>{s.total_sources}</td>
                      <td>{s.active_sources}</td>
                      <td>{s.real_time_sources}</td>
                      <td>{s.websocket_sources}</td>
                      <td>{s.avg_quality_rating?.toFixed(1)}/10</td>
                      <td>{s.total_monthly_cost > 0 ? `$${s.total_monthly_cost?.toLocaleString()}` : 'Free'}</td>
                      <td>{s.total_symbol_coverage?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Thomas Page - Thomas E-commerce Domains
function Thomas() {
  const [domains, setDomains] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  // Thomas domains on Pluto
  const thomasDomains = ['3fuel.com', 'food33.com', 'kmkle.com', 'metal33.com', 'oil33.com', 'qqarbon.com', 'rb0t.com', 'solrze.com', 'syrvyr.com'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [domainsRes, analyticsRes] = await Promise.all([
        fetch(API_BASE + '/domains'),
        fetch(API_BASE + '/analytics/summary')
      ]);

      const domainsData = await domainsRes.json();
      const analyticsData = await analyticsRes.json();

      if (analyticsData.success) setAnalytics(analyticsData.data);
      if (domainsData.success) {
        const filtered = domainsData.domains.filter(d => thomasDomains.includes(d.domain_name));
        const sorted = filtered.sort((a, b) => {
          const aStats = analyticsData.data[a.domain_name] || analyticsData.data['www.' + a.domain_name] || { realSessions: 0 };
          const bStats = analyticsData.data[b.domain_name] || analyticsData.data['www.' + b.domain_name] || { realSessions: 0 };
          return bStats.realSessions - aStats.realSessions;
        });
        setDomains(sorted);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const getAnalyticsForDomain = (domain) => {
    return analytics[domain] || analytics['www.' + domain] || { sessions: 0, engagedSessions: 0, users: 0, pageviews: 0, realSessions: 0 };
  };

  const totalStats = domains.reduce((acc, d) => {
    const stats = getAnalyticsForDomain(d.domain_name);
    return {
      sessions: acc.sessions + stats.sessions,
      engagedSessions: acc.engagedSessions + stats.engagedSessions,
      realSessions: acc.realSessions + stats.realSessions,
      users: acc.users + stats.users,
      pageviews: acc.pageviews + stats.pageviews
    };
  }, { sessions: 0, engagedSessions: 0, realSessions: 0, users: 0, pageviews: 0 });

  return (
    <>
      <Helmet>
        <title>Thomas - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Thomas E-commerce</h2>
        <p className="subtitle">React e-commerce domains on Pluto (last 7 days)</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="stats-summary">
              <div className="stat-box">
                <span className="stat-value">{domains.length}</span>
                <span className="stat-label">Domains</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.realSessions.toLocaleString()}</span>
                <span className="stat-label">Real Sessions</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.engagedSessions.toLocaleString()}</span>
                <span className="stat-label">Engaged</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.pageviews.toLocaleString()}</span>
                <span className="stat-label">Pageviews</span>
              </div>
            </div>

            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Description</th>
                    <th>Sessions</th>
                    <th>Engaged</th>
                    <th>Real</th>
                    <th>Pageviews</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map(d => {
                    const stats = getAnalyticsForDomain(d.domain_name);
                    return (
                      <tr key={d.domain_name}>
                        <td>
                          <a href={"https://" + d.domain_name} target="_blank" rel="noopener noreferrer">
                            {d.domain_name}
                          </a>
                        </td>
                        <td className="description">{d.company_name || '-'}</td>
                        <td>{stats.sessions.toLocaleString()}</td>
                        <td>{stats.engagedSessions.toLocaleString()}</td>
                        <td>{stats.realSessions.toLocaleString()}</td>
                        <td>{stats.pageviews.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// AI Page
function AI() {
  const [domains, setDomains] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  const aiDomains = ['pllur.com', 'rprtz.com', 'bundalini.com', 'gr11d.com', 'elyvt.com', 'lyfter.com'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [domainsRes, analyticsRes] = await Promise.all([
        fetch(API_BASE + '/domains'),
        fetch(API_BASE + '/analytics/summary')
      ]);

      const domainsData = await domainsRes.json();
      const analyticsData = await analyticsRes.json();

      if (domainsData.success) {
        setDomains(domainsData.domains.filter(d => aiDomains.includes(d.domain_name)));
      }
      if (analyticsData.success) setAnalytics(analyticsData.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const getAnalyticsForDomain = (domain) => {
    return analytics[domain] || analytics['www.' + domain] || { sessions: 0, engagedSessions: 0, users: 0, pageviews: 0, realSessions: 0 };
  };

  const totalStats = domains.reduce((acc, d) => {
    const stats = getAnalyticsForDomain(d.domain_name);
    return {
      realSessions: acc.realSessions + stats.realSessions,
      pageviews: acc.pageviews + stats.pageviews
    };
  }, { realSessions: 0, pageviews: 0 });

  return (
    <>
      <Helmet>
        <title>AI - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>AI</h2>
        <p className="subtitle">AI projects (last 7 days)</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="stats-summary">
              <div className="stat-box">
                <span className="stat-value">{domains.length}</span>
                <span className="stat-label">Domains</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.realSessions.toLocaleString()}</span>
                <span className="stat-label">Real Sessions</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.pageviews.toLocaleString()}</span>
                <span className="stat-label">Pageviews</span>
              </div>
            </div>

            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Description</th>
                    <th>Real</th>
                    <th>Pageviews</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map(d => {
                    const stats = getAnalyticsForDomain(d.domain_name);
                    return (
                      <tr key={d.domain_name}>
                        <td>
                          <a href={"https://" + d.domain_name} target="_blank" rel="noopener noreferrer">
                            {d.domain_name}
                          </a>
                        </td>
                        <td className="description">{d.description || d.purpose || d.company_name || '-'}</td>
                        <td>{stats.realSessions.toLocaleString()}</td>
                        <td>{stats.pageviews.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// OneWerx Page
function OneWerx() {
  const [domains, setDomains] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  const onewerxDomains = ['onewerx.com', 'pmley.com'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [domainsRes, analyticsRes] = await Promise.all([
        fetch(API_BASE + '/domains'),
        fetch(API_BASE + '/analytics/summary')
      ]);

      const domainsData = await domainsRes.json();
      const analyticsData = await analyticsRes.json();

      if (domainsData.success) {
        setDomains(domainsData.domains.filter(d => onewerxDomains.includes(d.domain_name)));
      }
      if (analyticsData.success) setAnalytics(analyticsData.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const getAnalyticsForDomain = (domain) => {
    return analytics[domain] || analytics['www.' + domain] || { sessions: 0, engagedSessions: 0, users: 0, pageviews: 0, realSessions: 0 };
  };

  const totalStats = domains.reduce((acc, d) => {
    const stats = getAnalyticsForDomain(d.domain_name);
    return {
      realSessions: acc.realSessions + stats.realSessions,
      pageviews: acc.pageviews + stats.pageviews
    };
  }, { realSessions: 0, pageviews: 0 });

  return (
    <>
      <Helmet>
        <title>OneWerx - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>OneWerx</h2>
        <p className="subtitle">Digital agency client work (last 7 days)</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="stats-summary">
              <div className="stat-box">
                <span className="stat-value">{totalStats.realSessions.toLocaleString()}</span>
                <span className="stat-label">Real Sessions</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.pageviews.toLocaleString()}</span>
                <span className="stat-label">Pageviews</span>
              </div>
            </div>

            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Description</th>
                    <th>Real</th>
                    <th>Pageviews</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map(d => {
                    const stats = getAnalyticsForDomain(d.domain_name);
                    return (
                      <tr key={d.domain_name}>
                        <td>
                          <a href={"https://" + d.domain_name} target="_blank" rel="noopener noreferrer">
                            {d.domain_name}
                          </a>
                        </td>
                        <td className="description">{d.description || d.purpose || d.company_name || '-'}</td>
                        <td>{stats.realSessions.toLocaleString()}</td>
                        <td>{stats.pageviews.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Personal Page
function Personal() {
  const [domains, setDomains] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  const personalDomains = ['asheram.com', 'ynglee.com', 'resultt.com', 'bllnc.com', 'blissark.com', 'ivoze.com', 'rrupa.com'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [domainsRes, analyticsRes] = await Promise.all([
        fetch(API_BASE + '/domains'),
        fetch(API_BASE + '/analytics/summary')
      ]);

      const domainsData = await domainsRes.json();
      const analyticsData = await analyticsRes.json();

      if (domainsData.success) {
        setDomains(domainsData.domains.filter(d => personalDomains.includes(d.domain_name)));
      }
      if (analyticsData.success) setAnalytics(analyticsData.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const getAnalyticsForDomain = (domain) => {
    return analytics[domain] || analytics['www.' + domain] || { sessions: 0, engagedSessions: 0, users: 0, pageviews: 0, realSessions: 0 };
  };

  const totalStats = domains.reduce((acc, d) => {
    const stats = getAnalyticsForDomain(d.domain_name);
    return {
      realSessions: acc.realSessions + stats.realSessions,
      pageviews: acc.pageviews + stats.pageviews
    };
  }, { realSessions: 0, pageviews: 0 });

  return (
    <>
      <Helmet>
        <title>Personal - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Personal</h2>
        <p className="subtitle">Personal and client domains (last 7 days)</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="stats-summary">
              <div className="stat-box">
                <span className="stat-value">{domains.length}</span>
                <span className="stat-label">Domains</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.realSessions.toLocaleString()}</span>
                <span className="stat-label">Real Sessions</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.pageviews.toLocaleString()}</span>
                <span className="stat-label">Pageviews</span>
              </div>
            </div>

            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Owner</th>
                    <th>Purpose</th>
                    <th>Real</th>
                    <th>Pageviews</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map(d => {
                    const stats = getAnalyticsForDomain(d.domain_name);
                    return (
                      <tr key={d.domain_name}>
                        <td>
                          <a href={"https://" + d.domain_name} target="_blank" rel="noopener noreferrer">
                            {d.domain_name}
                          </a>
                        </td>
                        <td>{d.owner || '-'}</td>
                        <td className="description">{d.purpose || '-'}</td>
                        <td>{stats.realSessions.toLocaleString()}</td>
                        <td>{stats.pageviews.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// RE Page
function RE() {
  const [domains, setDomains] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  const reDomains = ['xp0e.com', 'worke.ca', 'equityfair.com', 'hhomez.com'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [domainsRes, analyticsRes] = await Promise.all([
        fetch(API_BASE + '/domains'),
        fetch(API_BASE + '/analytics/summary')
      ]);

      const domainsData = await domainsRes.json();
      const analyticsData = await analyticsRes.json();

      if (domainsData.success) {
        setDomains(domainsData.domains.filter(d => reDomains.includes(d.domain_name)));
      }
      if (analyticsData.success) setAnalytics(analyticsData.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const getAnalyticsForDomain = (domain) => {
    return analytics[domain] || analytics['www.' + domain] || { sessions: 0, engagedSessions: 0, users: 0, pageviews: 0, realSessions: 0 };
  };

  const totalStats = domains.reduce((acc, d) => {
    const stats = getAnalyticsForDomain(d.domain_name);
    return {
      realSessions: acc.realSessions + stats.realSessions,
      pageviews: acc.pageviews + stats.pageviews
    };
  }, { realSessions: 0, pageviews: 0 });

  return (
    <>
      <Helmet>
        <title>RE - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>RE</h2>
        <p className="subtitle">Real estate and service domains (last 7 days)</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="stats-summary">
              <div className="stat-box">
                <span className="stat-value">{domains.length}</span>
                <span className="stat-label">Domains</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.realSessions.toLocaleString()}</span>
                <span className="stat-label">Real Sessions</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{totalStats.pageviews.toLocaleString()}</span>
                <span className="stat-label">Pageviews</span>
              </div>
            </div>

            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Description</th>
                    <th>Real</th>
                    <th>Pageviews</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map(d => {
                    const stats = getAnalyticsForDomain(d.domain_name);
                    return (
                      <tr key={d.domain_name}>
                        <td>
                          <a href={"https://" + d.domain_name} target="_blank" rel="noopener noreferrer">
                            {d.domain_name}
                          </a>
                        </td>
                        <td className="description">{d.purpose || d.company_name || '-'}</td>
                        <td>{stats.realSessions.toLocaleString()}</td>
                        <td>{stats.pageviews.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// New Page - Newly created template sites
function New() {
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  const newDomains = [
    { domain: '47ly.com', description: 'Synthetic diamonds' },
    { domain: '4emd.com', description: 'Orthodontist marketing agency' },
    { domain: 'drnzz.com', description: 'Drone swarm programming - open source' },
    { domain: 'evcle.com', description: 'Chinese EV models and parts' },
    { domain: 'fbryk.com', description: 'Custom fabrication services' },
    { domain: 'fish999.com', description: 'Fishing gear and tackle' },
    { domain: 'fnzly.com', description: 'Personal finance tools' },
    { domain: 'kidzley.com', description: 'Kids toys and activities' },
    { domain: 'kosmae.com', description: 'USA cosmetics and apothecary' },
    { domain: 'llaundromat.com', description: 'Laundry pickup/delivery service' },
    { domain: 'luxrry.com', description: 'Luxury goods marketplace' },
    { domain: 'mmineral.com', description: 'USA minerals ecommerce' },
    { domain: 'rrenew.com', description: 'Renewable energy solutions' },
    { domain: 'snkre.com', description: 'Sneaker marketplace' },
    { domain: 'sprtze.com', description: 'Sports equipment and gear' },
    { domain: 'trlze.com', description: 'E-Bike sales and parts' }
  ];

  useEffect(() => {
    fetch(API_BASE + '/analytics/summary')
      .then(res => res.json())
      .then(data => {
        if (data.success) setAnalytics(data.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const getAnalyticsForDomain = (domain) => {
    return analytics[domain] || analytics['www.' + domain] || { sessions: 0, engagedSessions: 0, users: 0, pageviews: 0, realSessions: 0 };
  };

  const sortedDomains = [...newDomains].sort((a, b) => {
    const aStats = getAnalyticsForDomain(a.domain);
    const bStats = getAnalyticsForDomain(b.domain);
    return bStats.realSessions - aStats.realSessions;
  });

  const totalStats = newDomains.reduce((acc, d) => {
    const stats = getAnalyticsForDomain(d.domain);
    return {
      sessions: acc.sessions + stats.sessions,
      engagedSessions: acc.engagedSessions + stats.engagedSessions,
      realSessions: acc.realSessions + stats.realSessions,
      users: acc.users + stats.users,
      pageviews: acc.pageviews + stats.pageviews
    };
  }, { sessions: 0, engagedSessions: 0, realSessions: 0, users: 0, pageviews: 0 });

  return (
    <>
      <Helmet>
        <title>New - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>New</h2>
        <p className="subtitle">Newly created template sites (last 7 days)</p>

        <div className="stats-summary">
          <div className="stat-box">
            <span className="stat-value">{newDomains.length}</span>
            <span className="stat-label">Sites</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{totalStats.sessions.toLocaleString()}</span>
            <span className="stat-label">Sessions</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{totalStats.realSessions.toLocaleString()}</span>
            <span className="stat-label">Real</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{totalStats.pageviews.toLocaleString()}</span>
            <span className="stat-label">Pageviews</span>
          </div>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="domains-table">
            <table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Description</th>
                  <th>Sessions</th>
                  <th>Engaged</th>
                  <th>Real</th>
                  <th>Pageviews</th>
                </tr>
              </thead>
              <tbody>
                {sortedDomains.map(d => {
                  const stats = getAnalyticsForDomain(d.domain);
                  return (
                    <tr key={d.domain}>
                      <td>
                        <a href={"https://" + d.domain} target="_blank" rel="noopener noreferrer">
                          {d.domain}
                        </a>
                      </td>
                      <td className="description">{d.description}</td>
                      <td>{stats.sessions.toLocaleString()}</td>
                      <td>{stats.engagedSessions.toLocaleString()}</td>
                      <td>{stats.realSessions.toLocaleString()}</td>
                      <td>{stats.pageviews.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// Empty Categories Page
function EmptyCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetch(API_BASE + '/empty-categories')
      .then(res => res.json())
      .then(data => {
        if (data.success) setCategories(data.categories);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filteredCategories = filter
    ? categories.filter(c => c.name.toLowerCase().includes(filter.toLowerCase()))
    : categories;

  return (
    <>
      <Helmet>
        <title>Empty Categories - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Empty Categories</h2>
        <p className="subtitle">Categories without linked companies ({categories.length} total)</p>

        <div className="stats-summary">
          <div className="stat-box">
            <span className="stat-value">{categories.length}</span>
            <span className="stat-label">Empty</span>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Filter categories..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ padding: '8px 12px', width: '300px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="domains-table">
            <table>
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>URL Slug</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.slice(0, 100).map(c => (
                  <tr key={c.url_slug}>
                    <td>{c.name}</td>
                    <td className="description">{c.url_slug}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredCategories.length > 100 && (
              <p style={{ marginTop: '16px', color: '#666' }}>
                Showing 100 of {filteredCategories.length} categories. Use filter to narrow results.
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// Email Page - Local Mail Client
function Email() {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailContent, setEmailContent] = useState(null);

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      const res = await fetch(API_BASE + '/emails');
      const data = await res.json();
      if (data.success) setEmails(data.emails);
    } catch (err) {
      console.error('Error fetching emails:', err);
    }
    setLoading(false);
  };

  const viewEmail = async (emailId) => {
    try {
      const res = await fetch(API_BASE + '/emails/' + encodeURIComponent(emailId));
      const data = await res.json();
      if (data.success) {
        setSelectedEmail(emailId);
        setEmailContent(data.email);
      }
    } catch (err) {
      console.error('Error fetching email:', err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Email - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Email</h2>
        <p className="subtitle">Local mailbox - asher@syrvyr.com</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="email-container">
            <div className="email-list">
              <div className="stats-summary">
                <div className="stat-box">
                  <span className="stat-value">{emails.length}</span>
                  <span className="stat-label">Emails</span>
                </div>
                <button onClick={fetchEmails} className="refresh-btn">Refresh</button>
              </div>

              <div className="domains-table">
                <table>
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>Subject</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map(email => (
                      <tr key={email.id} className={selectedEmail === email.id ? 'selected' : ''}>
                        <td title={email.from} style={{maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          {email.from.split('<')[0].trim() || email.from}
                        </td>
                        <td style={{maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          <button onClick={() => viewEmail(email.id)} className="link-btn">{email.subject}</button>
                        </td>
                        <td style={{whiteSpace: 'nowrap'}}>{email.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {emailContent && (
              <div className="email-viewer">
                <div className="email-header">
                  <button onClick={() => { setSelectedEmail(null); setEmailContent(null); }} className="close-btn">×</button>
                  <h3>{emailContent.subject}</h3>
                  <p><strong>From:</strong> {emailContent.from}</p>
                  <p><strong>To:</strong> {emailContent.to}</p>
                  <p><strong>Date:</strong> {emailContent.date}</p>
                </div>
                <div className="email-body">
                  <pre style={{whiteSpace: 'pre-wrap', fontFamily: 'inherit'}}>{emailContent.body}</pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// Gmail Page - Synced starred emails from Gmail
function Gmail() {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailContent, setEmailContent] = useState(null);
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: null });
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  useEffect(() => {
    checkGmailStatus();
  }, []);

  const checkGmailStatus = async () => {
    try {
      const res = await fetch(API_BASE + '/gmail/status');
      const data = await res.json();
      setGmailStatus(data);
      fetchEmails();
    } catch (err) {
      console.error('Error checking Gmail status:', err);
      setLoading(false);
    }
  };

  const fetchEmails = async () => {
    try {
      const res = await fetch(API_BASE + '/gmail/emails');
      const data = await res.json();
      if (data.success) setEmails(data.emails);
    } catch (err) {
      console.error('Error fetching emails:', err);
    }
    setLoading(false);
  };

  const syncGmail = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const res = await fetch(API_BASE + '/gmail/sync');
      const data = await res.json();
      if (data.success) {
        setSyncMessage(`Synced ${data.synced} new starred emails`);
        fetchEmails();
      } else {
        setSyncMessage('Sync failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setSyncMessage('Sync error: ' + err.message);
    }
    setSyncing(false);
  };

  const viewEmail = async (emailId) => {
    try {
      const res = await fetch(API_BASE + '/gmail/email/' + encodeURIComponent(emailId));
      const data = await res.json();
      if (data.success) {
        setSelectedEmail(emailId);
        setEmailContent(data.email);
      }
    } catch (err) {
      console.error('Error fetching email:', err);
    }
  };

  const connectGmail = () => {
    window.location.href = API_BASE + '/gmail/auth';
  };

  return (
    <>
      <Helmet>
        <title>Gmail - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Gmail</h2>
        <p className="subtitle">
          {gmailStatus.connected ? `Connected: ${gmailStatus.email}` : 'Not connected'}
        </p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="email-container">
            <div className="email-list">
              <div className="stats-summary">
                <div className="stat-box">
                  <span className="stat-value">{emails.length}</span>
                  <span className="stat-label">Starred</span>
                </div>
                {!gmailStatus.connected ? (
                  <button onClick={connectGmail} className="gmail-btn">Connect Gmail</button>
                ) : (
                  <button onClick={syncGmail} className="gmail-btn" disabled={syncing}>
                    {syncing ? 'Syncing...' : 'Sync Starred'}
                  </button>
                )}
                {syncMessage && <span className="sync-message">{syncMessage}</span>}
              </div>

              <div className="domains-table">
                <table>
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>Subject</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map(email => (
                      <tr key={email.id} className={selectedEmail === email.id ? 'selected' : ''}>
                        <td title={email.from} style={{maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          {email.from ? email.from.split('<')[0].trim() : ''}
                        </td>
                        <td style={{maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          <button onClick={() => viewEmail(email.id)} className="link-btn">{email.subject}</button>
                        </td>
                        <td style={{whiteSpace: 'nowrap'}}>{email.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {emailContent && (
              <div className="email-viewer">
                <div className="email-header">
                  <button onClick={() => { setSelectedEmail(null); setEmailContent(null); }} className="close-btn">×</button>
                  <h3>{emailContent.subject}</h3>
                  <p><strong>From:</strong> {emailContent.from}</p>
                  <p><strong>To:</strong> {emailContent.to}</p>
                  <p><strong>Date:</strong> {emailContent.date}</p>
                </div>
                <div className="email-body" dangerouslySetInnerHTML={{ __html: emailContent.body }} />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// 2Do Page - Todo items from Gmail (emails with "2do" subject)
function ToDo() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    try {
      const res = await fetch(API_BASE + '/gmail/2do');
      const data = await res.json();
      if (data.success) setTodos(data.emails);
    } catch (err) {
      console.error('Error fetching todos:', err);
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>2Do - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>2Do</h2>
        <p className="subtitle">Todo items from Gmail (subject: 2do)</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="todo-list">
            <div className="stats-summary">
              <div className="stat-box">
                <span className="stat-value">{todos.length}</span>
                <span className="stat-label">Items</span>
              </div>
              <button onClick={fetchTodos} className="refresh-btn">Refresh</button>
            </div>

            {todos.length === 0 ? (
              <p style={{color: '#666', marginTop: '20px'}}>No todo items found. Star emails with "2do" in the subject line.</p>
            ) : (
              <div style={{marginTop: '20px'}}>
                {todos.map(todo => (
                  <div key={todo.id} style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '10px'}}>
                    <div style={{fontSize: '0.85em', color: '#666', marginBottom: '5px'}}>{todo.date}</div>
                    <div style={{whiteSpace: 'pre-wrap', fontFamily: 'inherit'}}>{todo.body || '(no content)'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// YouTube Page - Synced playlists
function YouTube() {
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistItems, setPlaylistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [youtubeStatus, setYoutubeStatus] = useState({ connected: false, channel: null });
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  useEffect(() => {
    checkYoutubeStatus();
  }, []);

  const checkYoutubeStatus = async () => {
    try {
      const res = await fetch(API_BASE + '/youtube/status');
      const data = await res.json();
      setYoutubeStatus(data);
      fetchPlaylists();
    } catch (err) {
      console.error('Error checking YouTube status:', err);
      setLoading(false);
    }
  };

  const fetchPlaylists = async () => {
    try {
      const res = await fetch(API_BASE + '/youtube/playlists');
      const data = await res.json();
      if (data.success) setPlaylists(data.playlists);
    } catch (err) {
      console.error('Error fetching playlists:', err);
    }
    setLoading(false);
  };

  const syncYoutube = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const res = await fetch(API_BASE + '/youtube/sync');
      const data = await res.json();
      if (data.success) {
        setSyncMessage(`Synced ${data.playlists} playlists, ${data.items} videos`);
        fetchPlaylists();
      } else {
        setSyncMessage('Sync failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setSyncMessage('Sync error: ' + err.message);
    }
    setSyncing(false);
  };

  const viewPlaylist = async (playlistId) => {
    setSelectedPlaylist(playlistId);
    try {
      const res = await fetch(API_BASE + '/youtube/playlist/' + encodeURIComponent(playlistId));
      const data = await res.json();
      if (data.success) {
        setPlaylistItems(data.items);
      }
    } catch (err) {
      console.error('Error fetching playlist items:', err);
    }
  };

  const connectYoutube = () => {
    window.location.href = API_BASE + '/youtube/auth';
  };

  const selectedPlaylistData = playlists.find(p => p.id === selectedPlaylist);

  return (
    <>
      <Helmet>
        <title>YouTube - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>YouTube</h2>
        <p className="subtitle">
          {youtubeStatus.connected ? `Connected: ${youtubeStatus.channel}` : 'Not connected'}
        </p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="email-container">
            <div className="email-list">
              <div className="stats-summary">
                <div className="stat-box">
                  <span className="stat-value">{playlists.length}</span>
                  <span className="stat-label">Playlists</span>
                </div>
                {!youtubeStatus.connected ? (
                  <button onClick={connectYoutube} className="gmail-btn">Connect YouTube</button>
                ) : (
                  <button onClick={syncYoutube} className="gmail-btn" disabled={syncing}>
                    {syncing ? 'Syncing...' : 'Sync Playlists'}
                  </button>
                )}
                {syncMessage && <span className="sync-message">{syncMessage}</span>}
              </div>

              <div className="domains-table">
                <table>
                  <thead>
                    <tr>
                      <th>Playlist</th>
                      <th>Videos</th>
                      <th>Privacy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {playlists.map(playlist => (
                      <tr key={playlist.id} className={selectedPlaylist === playlist.id ? 'selected' : ''}>
                        <td>
                          <button onClick={() => viewPlaylist(playlist.id)} className="link-btn">{playlist.title}</button>
                        </td>
                        <td>{playlist.item_count}</td>
                        <td>{playlist.privacy_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {selectedPlaylist && (
              <div className="email-viewer">
                <div className="email-header">
                  <button onClick={() => { setSelectedPlaylist(null); setPlaylistItems([]); }} className="close-btn">×</button>
                  <h3>{selectedPlaylistData?.title}</h3>
                  <p>{playlistItems.length} videos</p>
                </div>
                <div className="email-body" style={{maxHeight: '500px'}}>
                  {playlistItems.map(item => (
                    <div key={item.id} style={{display: 'flex', gap: '10px', marginBottom: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '6px'}}>
                      {item.thumbnail_url && (
                        <img src={item.thumbnail_url} alt="" style={{width: '120px', height: '68px', objectFit: 'cover', borderRadius: '4px'}} />
                      )}
                      <div style={{flex: 1}}>
                        <a href={`https://youtube.com/watch?v=${item.video_id}`} target="_blank" rel="noopener noreferrer" style={{fontWeight: 'bold', color: '#2c3e50', textDecoration: 'none'}}>
                          {item.title}
                        </a>
                        <div style={{fontSize: '0.85em', color: '#666'}}>{item.channel_title}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// Music - AI-generated recommendations
function Music() {
  const [recommendations, setRecommendations] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedGenre]);

  const fetchData = async () => {
    try {
      const [recsRes, playlistsRes] = await Promise.all([
        fetch(API_BASE + `/music/recommendations?status=all${selectedGenre !== 'all' ? `&genre=${selectedGenre}` : ''}`),
        fetch(API_BASE + '/music/playlists')
      ]);
      const recsData = await recsRes.json();
      const playlistsData = await playlistsRes.json();
      if (recsData.success) {
        setRecommendations(recsData.recommendations);
        setGenres(recsData.genres);
      }
      if (playlistsData.success) setPlaylists(playlistsData.playlists);
    } catch (err) {
      console.error('Error fetching music data:', err);
    }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await fetch(API_BASE + `/music/recommendations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Music - AI Recommendations</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>🎵 AI Music Recommendations</h2>
        <p className="subtitle">Curated by Claude based on your YouTube playlists</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            {playlists.length > 0 && (
              <div className="stats-summary" style={{marginBottom: '20px'}}>
                <div className="stat-box">
                  <span className="stat-value">{recommendations.length}</span>
                  <span className="stat-label">Songs</span>
                </div>
                {playlists.map(p => (
                  <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="gmail-btn">
                    ▶ {p.title} playlist
                  </a>
                ))}
              </div>
            )}

            <div style={{marginBottom: '15px'}}>
              <select value={selectedGenre} onChange={(e) => setSelectedGenre(e.target.value)} style={{padding: '8px', borderRadius: '6px', border: '1px solid #ddd'}}>
                <option value="all">All Genres</option>
                {genres.map(g => (
                  <option key={g.genre} value={g.genre}>{g.genre} ({g.count})</option>
                ))}
              </select>
            </div>

            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Artist</th>
                    <th>Song</th>
                    <th>Genre</th>
                    <th>Reason</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendations.map(rec => (
                    <tr key={rec.id} className={rec.status !== 'pending' ? 'selected' : ''}>
                      <td><strong>{rec.artist}</strong></td>
                      <td>
                        {rec.youtube_url ? (
                          <a href={rec.youtube_url} target="_blank" rel="noopener noreferrer" style={{color: '#c00'}}>{rec.song_title}</a>
                        ) : rec.song_title}
                      </td>
                      <td><span style={{background: '#eee', padding: '2px 8px', borderRadius: '10px', fontSize: '0.85em'}}>{rec.genre}</span></td>
                      <td style={{fontSize: '0.9em', color: '#666'}}>{rec.reason}</td>
                      <td>
                        {rec.status === 'pending' ? (
                          <>
                            <button onClick={() => updateStatus(rec.id, 'listened')} className="link-btn" style={{marginRight: '5px'}}>✓</button>
                            <button onClick={() => updateStatus(rec.id, 'skipped')} className="link-btn">✗</button>
                          </>
                        ) : (
                          <span style={{color: rec.status === 'listened' ? 'green' : '#999'}}>{rec.status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Movies - AI-generated recommendations
function Movies() {
  const [recommendations, setRecommendations] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedGenre]);

  const fetchData = async () => {
    try {
      const res = await fetch(API_BASE + `/movies/recommendations?status=all${selectedGenre !== 'all' ? `&genre=${selectedGenre}` : ''}`);
      const data = await res.json();
      if (data.success) {
        setRecommendations(data.recommendations);
        setGenres(data.genres);
      }
    } catch (err) {
      console.error('Error fetching movie data:', err);
    }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await fetch(API_BASE + `/movies/recommendations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Movies - AI Recommendations</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>🎬 AI Movie Recommendations</h2>
        <p className="subtitle">Curated by Claude based on your film playlist</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="stats-summary" style={{marginBottom: '20px'}}>
              <div className="stat-box">
                <span className="stat-value">{recommendations.length}</span>
                <span className="stat-label">Movies</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{recommendations.filter(r => r.video_id).length}</span>
                <span className="stat-label">With Trailers</span>
              </div>
            </div>

            <div style={{marginBottom: '15px'}}>
              <select value={selectedGenre} onChange={(e) => setSelectedGenre(e.target.value)} style={{padding: '8px', borderRadius: '6px', border: '1px solid #ddd'}}>
                <option value="all">All Genres</option>
                {genres.map(g => (
                  <option key={g.genre} value={g.genre}>{g.genre} ({g.count})</option>
                ))}
              </select>
            </div>

            <div className="domains-table">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Director</th>
                    <th>Year</th>
                    <th>Genre</th>
                    <th>Reason</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendations.map(rec => (
                    <tr key={rec.id} className={rec.status !== 'pending' ? 'selected' : ''}>
                      <td>
                        <strong>
                          {rec.youtube_url ? (
                            <a href={rec.youtube_url} target="_blank" rel="noopener noreferrer" style={{color: '#c00'}}>{rec.title}</a>
                          ) : rec.title}
                        </strong>
                      </td>
                      <td>{rec.director}</td>
                      <td>{rec.year}</td>
                      <td><span style={{background: '#eee', padding: '2px 8px', borderRadius: '10px', fontSize: '0.85em'}}>{rec.genre}</span></td>
                      <td style={{fontSize: '0.9em', color: '#666'}}>{rec.reason}</td>
                      <td>
                        {rec.status === 'pending' ? (
                          <>
                            <button onClick={() => updateStatus(rec.id, 'watched')} className="link-btn" style={{marginRight: '5px'}}>✓</button>
                            <button onClick={() => updateStatus(rec.id, 'skipped')} className="link-btn">✗</button>
                          </>
                        ) : (
                          <span style={{color: rec.status === 'watched' ? 'green' : '#999'}}>{rec.status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Calendar
function Calendar() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, email: null });
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  useEffect(() => {
    checkCalendarStatus();
  }, []);

  const checkCalendarStatus = async () => {
    try {
      const res = await fetch(API_BASE + '/calendar/status');
      const data = await res.json();
      setCalendarStatus(data);
      fetchEvents();
    } catch (err) {
      console.error('Error checking Calendar status:', err);
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await fetch(API_BASE + '/calendar/events');
      const data = await res.json();
      if (data.success) setEvents(data.events);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
    setLoading(false);
  };

  const syncCalendar = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const res = await fetch(API_BASE + '/calendar/sync');
      const data = await res.json();
      if (data.success) {
        setSyncMessage(`Synced ${data.synced} events`);
        fetchEvents();
      } else {
        setSyncMessage('Sync failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setSyncMessage('Sync error: ' + err.message);
    }
    setSyncing(false);
  };

  const connectCalendar = () => {
    window.location.href = API_BASE + '/calendar/auth';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr.replace(' ', 'T'));
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr.replace(' ', 'T'));
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const eventDate = new Date(dateStr.replace(' ', 'T')).toDateString();
    return eventDate === new Date().toDateString();
  };

  const isTomorrow = (dateStr) => {
    if (!dateStr) return false;
    const eventDate = new Date(dateStr.replace(' ', 'T')).toDateString();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return eventDate === tomorrow.toDateString();
  };

  return (
    <>
      <Helmet>
        <title>Calendar - Asher Swing</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Calendar</h2>
        <p className="subtitle">
          {calendarStatus.connected ? `Connected: ${calendarStatus.email}` : 'Not connected'}
        </p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="email-container">
            <div className="email-list">
              <div className="stats-summary">
                <div className="stat-box">
                  <span className="stat-value">{events.length}</span>
                  <span className="stat-label">Events</span>
                </div>
                {!calendarStatus.connected ? (
                  <button onClick={connectCalendar} className="gmail-btn">Connect Calendar</button>
                ) : (
                  <button onClick={syncCalendar} className="gmail-btn" disabled={syncing}>
                    {syncing ? 'Syncing...' : 'Sync Events'}
                  </button>
                )}
                {syncMessage && <span className="sync-message">{syncMessage}</span>}
              </div>

              <div className="domains-table">
                <table>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Date</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(event => (
                      <tr key={event.id}
                          className={`${selectedEvent === event.id ? 'selected' : ''} ${isToday(event.start_time) ? 'highlight-today' : ''}`}
                          style={isToday(event.start_time) ? {background: '#e8f5e9'} : isTomorrow(event.start_time) ? {background: '#fff3e0'} : {}}>
                        <td>
                          <button onClick={() => setSelectedEvent(selectedEvent === event.id ? null : event.id)} className="link-btn">
                            {event.summary}
                          </button>
                        </td>
                        <td>{formatDate(event.start_time)}</td>
                        <td>{event.all_day ? 'All day' : formatTime(event.start_time)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {selectedEvent && (
              <div className="email-viewer">
                {events.filter(e => e.id === selectedEvent).map(event => (
                  <div key={event.id}>
                    <div className="email-header">
                      <button onClick={() => setSelectedEvent(null)} className="close-btn">×</button>
                      <h3>{event.summary}</h3>
                      <p>{formatDate(event.start_time)} {!event.all_day && formatTime(event.start_time)}</p>
                      {event.location && <p style={{color: '#666'}}>{event.location}</p>}
                    </div>
                    <div className="email-body">
                      {event.description ? (
                        <div dangerouslySetInnerHTML={{__html: event.description.replace(/\n/g, '<br>')}} />
                      ) : (
                        <p style={{color: '#999'}}>No description</p>
                      )}
                      {event.html_link && (
                        <p style={{marginTop: '20px'}}>
                          <a href={event.html_link} target="_blank" rel="noopener noreferrer">Open in Google Calendar</a>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// Google Business Profile Management
function Maps() {
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapsStatus, setMapsStatus] = useState({ connected: false, email: null });
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [thomasSites, setThomasSites] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newListing, setNewListing] = useState({
    domain: '',
    business_name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    category: '',
    description: ''
  });

  useEffect(() => {
    checkMapsStatus();
    fetchThomasSites();
  }, []);

  const checkMapsStatus = async () => {
    try {
      const res = await fetch(API_BASE + '/maps/status');
      const data = await res.json();
      setMapsStatus(data);
      fetchListings();
    } catch (err) {
      console.error('Error checking Maps status:', err);
      setLoading(false);
    }
  };

  const fetchListings = async () => {
    try {
      const res = await fetch(API_BASE + '/maps/listings');
      const data = await res.json();
      if (data.success) setListings(data.listings || []);
    } catch (err) {
      console.error('Error fetching listings:', err);
    }
    setLoading(false);
  };

  const fetchThomasSites = async () => {
    try {
      const res = await fetch(API_BASE + '/maps/thomas-sites');
      const data = await res.json();
      if (data.success) setThomasSites(data.sites || []);
    } catch (err) {
      console.error('Error fetching thomas sites:', err);
    }
  };

  const syncListings = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const res = await fetch(API_BASE + '/maps/sync');
      const data = await res.json();
      if (data.success) {
        setSyncMessage(`Synced ${data.synced} listings`);
        fetchListings();
      } else {
        setSyncMessage('Sync failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setSyncMessage('Sync error: ' + err.message);
    }
    setSyncing(false);
  };

  const connectMaps = () => {
    window.location.href = API_BASE + '/maps/auth';
  };

  const handleAddListing = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(API_BASE + '/maps/listings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newListing)
      });
      const data = await res.json();
      if (data.success) {
        setSyncMessage('Listing added successfully');
        setShowAddForm(false);
        setNewListing({ domain: '', business_name: '', address: '', city: '', state: '', zip: '', phone: '', category: '', description: '' });
        fetchListings();
      } else {
        setSyncMessage('Error: ' + (data.error || 'Failed to add listing'));
      }
    } catch (err) {
      setSyncMessage('Error: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return '#4caf50';
      case 'pending': return '#ff9800';
      case 'unverified': return '#f44336';
      default: return '#999';
    }
  };

  return (
    <>
      <Helmet>
        <title>Maps - Business Listings</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Google Business Profiles</h2>
        <p className="subtitle">
          {mapsStatus.connected ? `Connected: ${mapsStatus.email}` : 'Not connected'} | Manage Thomas site business listings
        </p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="email-container">
            <div className="email-list">
              <div className="stats-summary">
                <div className="stat-box">
                  <span className="stat-value">{listings.length}</span>
                  <span className="stat-label">Listings</span>
                </div>
                <div className="stat-box">
                  <span className="stat-value">{listings.filter(l => l.status === 'verified').length}</span>
                  <span className="stat-label">Verified</span>
                </div>
                <div className="stat-box">
                  <span className="stat-value">{thomasSites.length}</span>
                  <span className="stat-label">Thomas Sites</span>
                </div>
                {!mapsStatus.connected ? (
                  <button onClick={connectMaps} className="gmail-btn">Connect Google</button>
                ) : (
                  <>
                    <button onClick={syncListings} className="gmail-btn" disabled={syncing}>
                      {syncing ? 'Syncing...' : 'Sync Listings'}
                    </button>
                    <button onClick={() => setShowAddForm(!showAddForm)} className="gmail-btn" style={{marginLeft: '10px'}}>
                      {showAddForm ? 'Cancel' : '+ Add Listing'}
                    </button>
                  </>
                )}
                {syncMessage && <span className="sync-message" style={{marginLeft: '10px'}}>{syncMessage}</span>}
              </div>

              {showAddForm && (
                <div style={{background: '#f5f5f5', padding: '20px', marginBottom: '20px', borderRadius: '8px'}}>
                  <h3 style={{marginBottom: '15px'}}>Add New Business Listing</h3>
                  <form onSubmit={handleAddListing}>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                      <select value={newListing.domain} onChange={e => setNewListing({...newListing, domain: e.target.value})} required style={{padding: '8px'}}>
                        <option value="">Select Thomas Site</option>
                        {thomasSites.map(site => (
                          <option key={site.domain} value={site.domain}>{site.domain} - {site.company_name}</option>
                        ))}
                      </select>
                      <input type="text" placeholder="Business Name" value={newListing.business_name} onChange={e => setNewListing({...newListing, business_name: e.target.value})} required style={{padding: '8px'}} />
                      <input type="text" placeholder="Street Address" value={newListing.address} onChange={e => setNewListing({...newListing, address: e.target.value})} required style={{padding: '8px'}} />
                      <input type="text" placeholder="City" value={newListing.city} onChange={e => setNewListing({...newListing, city: e.target.value})} required style={{padding: '8px'}} />
                      <input type="text" placeholder="State" value={newListing.state} onChange={e => setNewListing({...newListing, state: e.target.value})} required style={{padding: '8px'}} />
                      <input type="text" placeholder="ZIP Code" value={newListing.zip} onChange={e => setNewListing({...newListing, zip: e.target.value})} required style={{padding: '8px'}} />
                      <input type="text" placeholder="Phone" value={newListing.phone} onChange={e => setNewListing({...newListing, phone: e.target.value})} style={{padding: '8px'}} />
                      <input type="text" placeholder="Category (e.g., Industrial Equipment Supplier)" value={newListing.category} onChange={e => setNewListing({...newListing, category: e.target.value})} style={{padding: '8px'}} />
                    </div>
                    <textarea placeholder="Business Description" value={newListing.description} onChange={e => setNewListing({...newListing, description: e.target.value})} style={{width: '100%', padding: '8px', marginTop: '10px', minHeight: '80px'}} />
                    <button type="submit" className="gmail-btn" style={{marginTop: '10px'}}>Create Listing</button>
                  </form>
                </div>
              )}

              <div className="domains-table">
                <table>
                  <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>Domain</th>
                      <th>Location</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map(listing => (
                      <tr key={listing.id}
                          className={selectedListing === listing.id ? 'selected' : ''}
                          onClick={() => setSelectedListing(selectedListing === listing.id ? null : listing.id)}>
                        <td>
                          <button className="link-btn">{listing.business_name}</button>
                        </td>
                        <td>{listing.domain}</td>
                        <td>{listing.city}, {listing.state}</td>
                        <td>
                          <span style={{color: getStatusColor(listing.status), fontWeight: 'bold'}}>
                            {listing.status || 'unknown'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {listings.length === 0 && (
                      <tr><td colSpan="4" style={{textAlign: 'center', color: '#999'}}>No listings yet. Add one or sync from Google.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {selectedListing && (
              <div className="email-viewer">
                {listings.filter(l => l.id === selectedListing).map(listing => (
                  <div key={listing.id}>
                    <div className="email-header">
                      <button onClick={() => setSelectedListing(null)} className="close-btn">×</button>
                      <h3>{listing.business_name}</h3>
                      <p style={{color: getStatusColor(listing.status)}}>{listing.status || 'Unknown status'}</p>
                    </div>
                    <div className="email-body">
                      <p><strong>Domain:</strong> {listing.domain}</p>
                      <p><strong>Address:</strong> {listing.address}</p>
                      <p><strong>City:</strong> {listing.city}, {listing.state} {listing.zip}</p>
                      {listing.phone && <p><strong>Phone:</strong> {listing.phone}</p>}
                      {listing.category && <p><strong>Category:</strong> {listing.category}</p>}
                      {listing.description && (
                        <>
                          <p><strong>Description:</strong></p>
                          <p style={{color: '#666'}}>{listing.description}</p>
                        </>
                      )}
                      {listing.google_place_id && (
                        <p style={{marginTop: '20px'}}>
                          <a href={`https://www.google.com/maps/place/?q=place_id:${listing.google_place_id}`} target="_blank" rel="noopener noreferrer">View on Google Maps</a>
                        </p>
                      )}
                      {listing.gbp_url && (
                        <p>
                          <a href={listing.gbp_url} target="_blank" rel="noopener noreferrer">Manage in Google Business Profile</a>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// Google Shopping / Merchant Center
function Shopping() {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shoppingStatus, setShoppingStatus] = useState({ connected: false, email: null, merchant_id: null });
  const [accounts, setAccounts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [summary, setSummary] = useState({});
  const [totalProducts, setTotalProducts] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    checkShoppingStatus();
  }, []);

  useEffect(() => {
    if (shoppingStatus.connected && shoppingStatus.merchant_id) {
      fetchProducts();
      fetchSummary();
    }
  }, [shoppingStatus.merchant_id, page]);

  const checkShoppingStatus = async () => {
    try {
      const res = await fetch(API_BASE + '/shopping/status');
      const data = await res.json();
      setShoppingStatus(data);
      if (data.connected && !data.merchant_id) {
        fetchAccounts();
      }
      setLoading(false);
    } catch (err) {
      console.error('Error checking Shopping status:', err);
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await fetch(API_BASE + '/shopping/accounts');
      const data = await res.json();
      if (data.success) setAccounts(data.accounts || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(API_BASE + `/shopping/products?page=${page}&limit=50`);
      const data = await res.json();
      if (data.success) {
        setProducts(data.products || []);
        setTotalProducts(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await fetch(API_BASE + '/shopping/status-summary');
      const data = await res.json();
      if (data.success) setSummary(data.summary || {});
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const connectShopping = () => {
    window.location.href = API_BASE + '/shopping/auth';
  };

  const setMerchant = async (merchantId) => {
    try {
      const res = await fetch(API_BASE + '/shopping/set-merchant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merchant_id: merchantId })
      });
      const data = await res.json();
      if (data.success) {
        setShoppingStatus({ ...shoppingStatus, merchant_id: merchantId });
        setMessage('Merchant account selected');
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const uploadProducts = async () => {
    if (selectedProducts.length === 0) {
      setMessage('No products selected');
      return;
    }
    setUploading(true);
    setMessage('');
    try {
      const res = await fetch(API_BASE + '/shopping/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: selectedProducts })
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`Uploaded ${data.uploaded} products${data.errors.length > 0 ? `. Errors: ${data.errors.length}` : ''}`);
        setSelectedProducts([]);
        fetchSummary();
      } else {
        setMessage('Error: ' + (data.error || 'Upload failed'));
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
    setUploading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploaded': return '#4caf50';
      case 'approved': return '#2196f3';
      case 'pending': return '#ff9800';
      case 'disapproved': return '#f44336';
      default: return '#999';
    }
  };

  return (
    <>
      <Helmet>
        <title>Shopping - Google Merchant Center</title>
      </Helmet>
      <div className="domain-monitor">
        <h2>Google Merchant Center</h2>
        <p className="subtitle">
          {shoppingStatus.connected ? `Connected: ${shoppingStatus.email}` : 'Not connected'} | Upload Thomas products to Google Shopping
        </p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="email-container">
            <div className="email-list">
              <div className="stats-summary">
                <div className="stat-box">
                  <span className="stat-value">{totalProducts.toLocaleString()}</span>
                  <span className="stat-label">Products w/Images</span>
                </div>
                <div className="stat-box">
                  <span className="stat-value">{summary.uploaded || 0}</span>
                  <span className="stat-label">Uploaded</span>
                </div>
                <div className="stat-box">
                  <span className="stat-value">{summary.approved || 0}</span>
                  <span className="stat-label">Approved</span>
                </div>
                <div className="stat-box">
                  <span className="stat-value">{summary.disapproved || 0}</span>
                  <span className="stat-label">Disapproved</span>
                </div>
                {!shoppingStatus.connected ? (
                  <button onClick={connectShopping} className="gmail-btn">Connect Google</button>
                ) : !shoppingStatus.merchant_id ? (
                  <span style={{color: '#ff9800'}}>Select Merchant Account below</span>
                ) : (
                  <>
                    <button onClick={uploadProducts} className="gmail-btn" disabled={uploading || selectedProducts.length === 0}>
                      {uploading ? 'Uploading...' : `Upload ${selectedProducts.length} Selected`}
                    </button>
                  </>
                )}
                {message && <span className="sync-message" style={{marginLeft: '10px'}}>{message}</span>}
              </div>

              {shoppingStatus.connected && !shoppingStatus.merchant_id && accounts.length > 0 && (
                <div style={{background: '#f5f5f5', padding: '20px', marginBottom: '20px', borderRadius: '8px'}}>
                  <h3 style={{marginBottom: '15px'}}>Select Merchant Center Account</h3>
                  {accounts.map((account, idx) => (
                    <button key={idx} onClick={() => setMerchant(account.merchantId || account.aggregatorId)}
                            className="gmail-btn" style={{margin: '5px'}}>
                      {account.merchantId ? `Merchant: ${account.merchantId}` : `Aggregator: ${account.aggregatorId}`}
                    </button>
                  ))}
                </div>
              )}

              {shoppingStatus.merchant_id && (
                <>
                  <div style={{marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center'}}>
                    <button onClick={selectAll} className="gmail-btn" style={{fontSize: '12px'}}>
                      {selectedProducts.length === products.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span style={{color: '#666', fontSize: '14px'}}>
                      Page {page} of {Math.ceil(totalProducts / 50)} ({totalProducts} total)
                    </span>
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="gmail-btn" style={{fontSize: '12px'}}>Prev</button>
                    <button onClick={() => setPage(p => p + 1)} disabled={page * 50 >= totalProducts} className="gmail-btn" style={{fontSize: '12px'}}>Next</button>
                  </div>

                  <div className="domains-table">
                    <table>
                      <thead>
                        <tr>
                          <th style={{width: '40px'}}></th>
                          <th style={{width: '60px'}}>Image</th>
                          <th>Product Name</th>
                          <th>Company</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map(product => (
                          <tr key={product.id}
                              className={selectedProducts.includes(product.id) ? 'selected' : ''}
                              onClick={() => toggleProduct(product.id)}
                              style={{cursor: 'pointer'}}>
                            <td>
                              <input type="checkbox" checked={selectedProducts.includes(product.id)} onChange={() => {}} />
                            </td>
                            <td>
                              {product.image_url && (
                                <img src={product.image_url} alt="" style={{width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px'}} />
                              )}
                            </td>
                            <td>
                              <span style={{fontSize: '13px'}}>{product.name?.substring(0, 60)}{product.name?.length > 60 ? '...' : ''}</span>
                            </td>
                            <td style={{fontSize: '12px', color: '#666'}}>{product.company_name}</td>
                          </tr>
                        ))}
                        {products.length === 0 && (
                          <tr><td colSpan="4" style={{textAlign: 'center', color: '#999'}}>No products with images found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// DJ Mixes Page
function DJ() {
  const mixcloudMixes = [
    { name: 'Mashup', url: 'https://www.mixcloud.com/asherswing/mashup/' },
    { name: 'Om', url: 'https://www.mixcloud.com/asherswing/om/' },
    { name: 'Sm', url: 'https://www.mixcloud.com/asherswing/sm/' },
    { name: '919', url: 'https://www.mixcloud.com/asherswing/919/' },
    { name: 'Jfk', url: 'https://www.mixcloud.com/asherswing/jfk/' }
  ];

  const soundcloudTracks = [
    { name: 'Rakhi', url: 'https://soundcloud.com/asherswing/rakhi', year: '2023' },
    { name: 'Seoul', url: 'https://soundcloud.com/asherswing/seoul', year: '2014' },
    { name: 'Gayoung', url: 'https://soundcloud.com/asherswing/gayoung', year: '2014' }
  ];

  const linkStyle = {
    display: 'block',
    padding: '12px 16px',
    background: '#f8f9fa',
    borderRadius: '6px',
    textDecoration: 'none',
    color: '#2c3e50',
    marginBottom: '8px',
    transition: 'background 0.2s'
  };

  return (
    <>
      <Helmet>
        <title>DJ - Asher Swing</title>
        <meta name="description" content="DJ mixes and music by Asher Swing" />
      </Helmet>
      <div className="blog-section">
        <h2>DJ Mixes</h2>
        <p className="subtitle">Electronic music mixes</p>

        <div style={{marginTop: '30px'}}>
          <h3 style={{marginBottom: '15px', color: '#5000ff'}}>Mixcloud</h3>
          {mixcloudMixes.map(mix => (
            <a key={mix.name} href={mix.url} target="_blank" rel="noopener noreferrer"
               style={linkStyle}
               onMouseOver={e => e.currentTarget.style.background = '#e9ecef'}
               onMouseOut={e => e.currentTarget.style.background = '#f8f9fa'}>
              {mix.name}
            </a>
          ))}
        </div>

        <div style={{marginTop: '30px'}}>
          <h3 style={{marginBottom: '15px', color: '#ff5500'}}>SoundCloud</h3>
          {soundcloudTracks.map(track => (
            <a key={track.name} href={track.url} target="_blank" rel="noopener noreferrer"
               style={linkStyle}
               onMouseOver={e => e.currentTarget.style.background = '#e9ecef'}
               onMouseOut={e => e.currentTarget.style.background = '#f8f9fa'}>
              {track.name} <span style={{color: '#999', fontSize: '14px'}}>({track.year})</span>
            </a>
          ))}
        </div>
      </div>
    </>
  );
}

// Blog List Page
function BlogList() {
  const formatContent = (content) => {
    return content.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
  };

  return (
    <>
      <Helmet>
        <title>Blog - Asher Swing</title>
        <meta name="description" content="Thoughts on spirituality, meditation, and yoga." />
      </Helmet>
      <div className="blog-section">
        <h2>Blog</h2>
        <p className="subtitle">Spirituality, meditation, yoga</p>
        <div className="posts-list">
          {posts.map(post => (
            <article key={post.id} className="post-card">
              <h3>{post.title.charAt(0).toUpperCase() + post.title.slice(1)}</h3>
              <div
                className="post-content"
                dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
              />
            </article>
          ))}
        </div>
      </div>
    </>
  );
}

function App() {
  return (
    <div className="container">
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Resume />} />
          <Route path="/resume" element={<Resume />} />
          <Route path="/manager" element={<Manager />} />
                    <Route path="/pages" element={<TopPages />} />
          <Route path="/sources" element={<Sources />} />
          <Route path="/trade" element={<Trade />} />
          <Route path="/thomas" element={<Thomas />} />
          <Route path="/onewerx" element={<OneWerx />} />
          <Route path="/ai" element={<AI />} />
          <Route path="/re" element={<RE />} />
          <Route path="/personal" element={<Personal />} />
          <Route path="/site" element={<New />} />
          <Route path="/empty-categories" element={<EmptyCategories />} />
          <Route path="/email" element={<Email />} />
          <Route path="/gmail" element={<Gmail />} />
          <Route path="/2do" element={<ToDo />} />
          <Route path="/youtube" element={<YouTube />} />
          <Route path="/music" element={<Music />} />
          <Route path="/movies" element={<Movies />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/maps" element={<Maps />} />
          <Route path="/shopping" element={<Shopping />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/dj" element={<DJ />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
